/**
 * Watch slice — TELL ME WHEN, MONITORING and the NOTICE BOARD's rows
 * (DESIGN-CHANGELOG 2026-09-03 "MY GAMES: the page about you"; docs/porting/
 * AA.md phase 4; migration 3063).
 *
 * `watch` is MY watches by game id — an ACCOUNT fact like the backlog: it
 * follows you across parties and nobody else reads it. `board` is the notice
 * board, replaced whole on every read: the party's scores (rows a trigger on
 * `game_scores` wrote) and the triggers I armed that fired (rows the nightly
 * `watch_sweep` wrote). Every board row is a persisted row (Marcus,
 * 2026-09-04); nothing on the board is computed from other state.
 *
 * TELL ME WHEN edits a DRAFT (`wmDraft`), committed by SAVE ▸ alone — the
 * prototype's `wmDraft` / `wSave`. MONITORING's lamps and its in-cell stepper
 * write immediately (`monTog` / `monCap`), each through `putWatch`. A record
 * with nothing armed is DELETED, never stored — the table's CHECK and the
 * prototype's `putWatch` agree.
 *
 * `monPin` is the row set MONITORING entered with: a row you just silenced
 * stays until you leave, so the last lamp does not pull the row out from
 * under the cursor.
 */

import type { StateCreator } from 'zustand';

import type { BoardEvent, GameId, TriggerKey, WatchRec } from '../../lib/types.ts';
import { anyArmed, armedKeys, capFor, EMPTY_WATCH, trNa } from '../../lib/watch.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';

export interface WatchSlice {
  /** my watches, by game id (string key) */
  watch: Record<string, WatchRec>;
  /** the notice board's rows, as the policy hands them back */
  board: BoardEvent[];

  /** TELL ME WHEN — the game the modal is open on; `0` = closed */
  wmId: GameId;
  wmDraft: WatchRec;
  /** `ALSO PUT IT ON MY BACKLOG` — offered only when the game is not shelved */
  wmAlso: boolean;
  /** MONITORING: its search and the pinned row set */
  monQ: string;
  monPin: GameId[];

  watchOf: (id: GameId) => WatchRec | undefined;

  wOpen: (id: GameId) => void;
  closeWatch: () => void;
  /** Toggle one trigger on the draft; a trigger the game cannot carry is inert. */
  wTog: (t: TriggerKey) => void;
  /** ± $1 on the draft's floor, never under $1. */
  wCap: (delta: number) => void;
  wAlsoTog: () => void;
  /** SAVE ▸ — commits the draft; nothing armed deletes the watch. */
  wSave: () => Promise<void>;

  /** MONITORING lamp: flips one trigger on a stored watch, immediately. */
  monTog: (id: GameId, t: TriggerKey) => Promise<void>;
  /** MONITORING's in-cell stepper. */
  monCap: (id: GameId, delta: number) => Promise<void>;
  /**
   * Column header click (desktop): arms the trigger on every pinned row that
   * can carry it; if every such row already has it, disarms it on all.
   */
  monColTog: (t: TriggerKey) => Promise<void>;
  setMonQ: (v: string) => void;
  /** Pin the current watched set — called on entering MONITORING. */
  monEnter: () => void;
}

export const createWatchSlice: StateCreator<QdStore, [], [], WatchSlice> = (set, get) => {
  /** One write, optimistic, keyed per game like a shelf write. */
  const put = async (id: GameId, next: WatchRec | null, toast?: string): Promise<void> => {
    const s = get();
    const rec = next && anyArmed(next) ? next : null;
    await mutate(storeApi(get, set), {
      key: 'watch:' + id,
      optimistic: (st) => {
        const w = { ...st.watch };
        if (rec) w[String(id)] = rec;
        else delete w[String(id)];
        return { watch: w };
      },
      commit: () => s.repo.putWatch(id, rec),
      ...(toast !== undefined ? { toast } : {}),
    });
  };

  return {
    watch: {},
    board: [],
    wmId: 0,
    wmDraft: { ...EMPTY_WATCH },
    wmAlso: false,
    monQ: '',
    monPin: [],

    watchOf: (id) => get().watch[String(id)],

    wOpen: (id) => {
      const s = get();
      const g = s.gById(id);
      const cur = s.watch[String(id)];
      // The draft starts from the stored record, or blank with the default
      // floor for this game's price.
      set({
        wmId: id,
        wmDraft: cur ? { ...cur } : { ...EMPTY_WATCH, cap: capFor(g) },
        wmAlso: false,
      });
    },
    closeWatch: () => set({ wmId: 0 }),
    wTog: (t) => {
      const s = get();
      const g = s.gById(s.wmId);
      if (g && trNa(g, t) !== '') return;
      set({ wmDraft: { ...s.wmDraft, [t]: !s.wmDraft[t] } });
    },
    wCap: (delta) => set((st) => ({ wmDraft: { ...st.wmDraft, cap: Math.max(1, st.wmDraft.cap + delta) } })),
    wAlsoTog: () => set((st) => ({ wmAlso: !st.wmAlso })),

    wSave: async () => {
      const s = get();
      const id = s.wmId;
      if (!id) return;
      const g = s.gById(id);
      const draft = { ...s.wmDraft };
      const also = s.wmAlso;
      // Close first, like the score modal: a slow commit never holds the form
      // over the tile that already shows the result.
      set({ wmId: 0 });
      const n = armedKeys(draft, g).length;
      const gn = g ? ' · ' + g.n.toUpperCase() : '';
      const armed = anyArmed(draft);
      await put(id, armed ? draft : null, armed ? 'WATCHING ' + n + gn : 'NOT WATCHING' + gn);
      if (also && armed && !get().soloHas(id)) await get().soloAdd(id);
    },

    monTog: async (id, t) => {
      const s = get();
      const g = s.gById(id);
      if (g && trNa(g, t) !== '') return;
      const cur = s.watch[String(id)] ?? { ...EMPTY_WATCH, cap: capFor(g) };
      await put(id, { ...cur, [t]: !cur[t] });
    },
    monCap: async (id, delta) => {
      const s = get();
      const cur = s.watch[String(id)];
      if (!cur) return;
      await put(id, { ...cur, cap: Math.max(1, cur.cap + delta) });
    },
    monColTog: async (t) => {
      const s = get();
      const rows = s.monPin
        .map((id) => ({ id, g: s.gById(id) }))
        .filter((x) => x.g && trNa(x.g, t) === '');
      if (rows.length === 0) return;
      const allOn = rows.every((x) => s.watch[String(x.id)]?.[t]);
      for (const x of rows) {
        const cur = s.watch[String(x.id)] ?? { ...EMPTY_WATCH, cap: capFor(x.g) };
        if (cur[t] === !allOn) continue;
        await put(x.id, { ...cur, [t]: !allOn });
      }
    },
    setMonQ: (v) => set({ monQ: v }),
    monEnter: () => {
      const s = get();
      set({ monPin: Object.keys(s.watch).map((k) => Number(k) as GameId), monQ: '' });
    },
  };
};
